import React, { useState } from 'react';
import { 
  UserX, Shield, Trash2, AlertCircle, CheckCircle2, 
  Clock, Mail, ArrowRight, HelpCircle, Smartphone, Globe, 
  Lock, Check, AlertOctagon, ArrowUpRight, ChevronDown, ChevronUp
} from 'lucide-react';
import { Link } from 'react-router-dom';
import Layout from '../../components/layout/Layout';
import backendApi from '../../services/backendApi';

const AccountDeletionPage = () => {
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    companyName: '',
    reason: 'Role change or departure',
    additionalNotes: '',
    confirmAcknowledged: false,
  });

  const [submitted, setSubmitted] = useState(false);
  const [resultTicket, setResultTicket] = useState('');
  const [submitError, setSubmitError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);


  const steps = [
    { num: "01", title: "Submit Request", desc: "Via this web form or in the mobile app" },
    { num: "02", title: "Verification", desc: "Identity & enterprise authorization check" },
    { num: "03", title: "Data Erasure", desc: "Permanent credential & profile wipe" },
    { num: "04", title: "Confirmation", desc: "Official confirmation sent within 30 days" },
  ];

  const faqs = [
    {
      q: "Can I request account deletion directly from inside the mobile app?",
      a: "Yes. Open the AxonMetric mobile app, tap the Navigation Drawer (sidebar), navigate to 'Manage Profile', and click 'Request Account Deletion' under the Legal & Compliance section."
    },
    {
      q: "How long does it take for my account data to be completely erased?",
      a: "All personal identifiers, credentials, and mobile device bindings are permanently deleted within 30 calendar days of verified submission."
    },
    {
      q: "Will deleting my account affect our solar plant's historical telemetry?",
      a: "No. Industrial SCADA generation numbers, inverter performance trends, and electrical logs belong to your organization and remain preserved. Only your personal credentials and identity bindings are erased."
    },
    {
      q: "Why are some safety Permit To Work (PTW) logs retained?",
      a: "Under statutory electricity grid safety regulations, completed High-Voltage isolation permits signed by certified engineers must be kept for auditing and liability inspection. Personal identifiers in these records are pseudonymized."
    }
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError('');
    if (!formData.confirmAcknowledged) {
      alert('Please acknowledge the data deletion terms before proceeding.');
      return;
    }

    setIsSubmitting(true);
    try {
      const res = await backendApi.submitDataDeletionRequest({
        full_name: formData.fullName,
        email: formData.email,
        company_name: formData.companyName,
        reason: formData.reason,
        additional_notes: formData.additionalNotes,
        source: 'WEB'
      });

      if (res && res.success) {
        setResultTicket(res.ticket_id);
        setSubmitted(true);
      } else {
        const errMsg = res?.message || (res?.errors ? Object.values(res.errors).flat().join(', ') : 'Failed to submit request. Please try again.');
        setSubmitError(errMsg);
      }
    } catch (err) {
      setSubmitError(err.message || 'An unexpected error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };


  return (
    <Layout header={6} footer={6}>
      <div className="legal-page min-h-screen font-sans">
        {/* Clean Website-Matched Hero Section */}
        <section className="relative pt-28 pb-16 lg:pt-32 lg:pb-20 bg-gradient-to-br from-white via-gray-50 to-white overflow-hidden border-b border-gray-200/70">
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full opacity-10" style={{ background: 'radial-gradient(circle, #EF4444 0%, transparent 70%)' }}></div>
            <div className="absolute -bottom-32 -left-32 w-96 h-96 rounded-full opacity-10" style={{ background: 'radial-gradient(circle, #1C4D88 0%, transparent 70%)' }}></div>
          </div>

          <div className="max-w-7xl mx-auto px-6 relative z-10">
            <div className="max-w-3xl">
              {/* Badge */}
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-gray-200 bg-white shadow-sm mb-6">
                <UserX className="w-4 h-4 text-red-600" />
                <span className="text-xs sm:text-sm font-extrabold text-gray-900">
                  Google Play & Apple App Store Mandatory Deletion Policy
                </span>
              </div>

              {/* Title */}
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-gray-900 leading-tight mb-5">
                Account & Data{' '}
                <span className="bg-gradient-to-r from-red-600 to-[#1C4D88] bg-clip-text text-transparent">
                  Deletion Portal
                </span>
              </h1>

              <p className="text-sm sm:text-base text-gray-600 leading-relaxed font-normal mb-8">
                In compliance with store developer requirements and data privacy regulations (DPDP Act 2023 & GDPR), AxonMetric provides a frictionless web-based method to delete your account without reinstalling the app.
              </p>

              <div className="flex flex-wrap items-center gap-3 pt-6 border-t border-gray-200 text-xs">
                <span className="text-gray-500 font-medium">Resolution Window: Within 30 Calendar Days</span>
                <span className="text-gray-300 hidden sm:inline">•</span>
                <Link
                  to="/privacy-policy"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 text-gray-700 font-semibold transition-colors shadow-sm"
                >
                  <Shield className="w-3.5 h-3.5 text-[#1C4D88]" /> View Privacy Policy
                </Link>
                <Link
                  to="/terms-and-conditions"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 text-gray-700 font-semibold transition-colors shadow-sm"
                >
                  View Terms & Conditions <ArrowUpRight className="w-3.5 h-3.5" />
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* 4-Step Process Bar */}
        <section className="max-w-7xl mx-auto px-6 py-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {steps.map((step, idx) => (
              <div
                key={idx}
                className="p-5 rounded-2xl bg-white border border-gray-200 shadow-sm flex flex-col justify-between"
              >
                <div className="flex items-center justify-between mb-3">
                  <span className="text-xl font-black text-[#1C4D88]">{step.num}</span>
                  <div className="w-2 h-2 rounded-full bg-blue-600" />
                </div>
                <div>
                  <h4 className="font-extrabold text-sm text-gray-900 mb-1">{step.title}</h4>
                  <p className="text-xs text-gray-500 leading-relaxed">{step.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Main Content & Interactive Form */}
        <section className="max-w-7xl mx-auto px-6 pb-20">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
            {/* Left Column: Guidelines & Policy Explanations */}
            <div className="lg:col-span-7 space-y-8">
              {/* Deletion Scope Matrix */}
              <div className="bg-white rounded-2xl border border-gray-200 p-6 sm:p-8 shadow-sm space-y-5">
                <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                  <Trash2 className="w-5 h-5 text-red-600" /> Data Erasure Breakdown
                </h3>

                <div className="space-y-3">
                  <div className="p-4 rounded-xl bg-red-50/60 border border-red-100 text-xs text-red-950 space-y-2">
                    <div className="font-bold flex items-center gap-2 text-red-900">
                      <CheckCircle2 className="w-4 h-4 text-red-600" /> Permanently Erased Data (Within 30 Days)
                    </div>
                    <ul className="space-y-1.5 list-disc pl-5 text-red-900/80">
                      <li>Login usernames, encrypted passwords, and active session tokens.</li>
                      <li>Full name, official designation, personal email address, and mobile number.</li>
                      <li>Device push tokens (Apple APNs & Google FCM) and biometric unlock bindings.</li>
                      <li>Local cache drafts and pending offline queue sync states.</li>
                    </ul>
                  </div>

                  <div className="p-4 rounded-xl bg-amber-50/60 border border-amber-100 text-xs text-amber-950 space-y-2">
                    <div className="font-bold flex items-center gap-2 text-amber-900">
                      <AlertOctagon className="w-4 h-4 text-amber-700" /> Statutory Grid Records (Preserved per Electrical Codes)
                    </div>
                    <p className="text-amber-900/80 leading-relaxed">
                      Completed electrical Permit To Work (PTW) safety checksheets and High-Voltage isolation logs previously signed by licensed engineers are legally preserved for mandatory grid safety auditing. Personal employee names in these records are pseudonymized.
                    </p>
                  </div>
                </div>
              </div>

              {/* Channels Grid */}
              <div className="bg-white rounded-2xl border border-gray-200 p-6 sm:p-8 shadow-sm space-y-4">
                <h3 className="text-lg font-bold text-gray-900">How to Submit Your Request</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-xs">
                  <div className="p-4 rounded-xl bg-gray-50 border border-gray-200 space-y-2">
                    <Smartphone className="w-5 h-5 text-[#1C4D88]" />
                    <h4 className="font-bold text-gray-900">Option 1: In the Mobile App</h4>
                    <p className="text-gray-600 leading-relaxed">
                      Open Sidebar Drawer &rarr; Tap <strong>Manage Profile</strong> &rarr; Click <strong>Request Account Deletion</strong> under Legal & Compliance.
                    </p>
                  </div>

                  <div className="p-4 rounded-xl bg-gray-50 border border-gray-200 space-y-2">
                    <Globe className="w-5 h-5 text-teal-600" />
                    <h4 className="font-bold text-gray-900">Option 2: Online Form</h4>
                    <p className="text-gray-600 leading-relaxed">
                      Fill out the form on this page. No mobile app re-installation or sign-in is required to complete this request.
                    </p>
                  </div>
                </div>
              </div>

              {/* Accordion FAQ */}
              <div className="bg-white rounded-2xl border border-gray-200 p-6 sm:p-8 shadow-sm space-y-4">
                <h3 className="text-lg font-bold text-gray-900">Frequently Asked Questions</h3>
                <div className="space-y-2">
                  {faqs.map((faq, idx) => (
                    <div key={idx} className="border border-gray-200 rounded-xl overflow-hidden">
                      <button
                        onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
                        className="w-full text-left px-4 py-3.5 bg-gray-50 hover:bg-gray-100 flex items-center justify-between text-xs font-bold text-gray-900 gap-2 transition-colors"
                      >
                        <span>{faq.q}</span>
                        {openFaq === idx ? <ChevronUp className="w-4 h-4 text-gray-500 shrink-0" /> : <ChevronDown className="w-4 h-4 text-gray-500 shrink-0" />}
                      </button>
                      {openFaq === idx && (
                        <div className="p-4 bg-white text-xs text-gray-600 leading-relaxed border-t border-gray-100">
                          {faq.a}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Right Column: Web Deletion Request Form */}
            <div className="lg:col-span-5">
              <div className="bg-white border border-gray-200 rounded-2xl p-6 sm:p-8 shadow-sm">
                {submitted ? (
                  <div className="text-center py-8 space-y-4">
                    <div className="w-16 h-16 bg-emerald-100 text-emerald-600 rounded-2xl flex items-center justify-center mx-auto">
                      <CheckCircle2 className="w-10 h-10" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900">Deletion Request Logged</h3>
                    <p className="text-xs text-gray-600 leading-relaxed">
                      Your data erasure ticket has been registered for <strong>{formData.email}</strong>. Our security and privacy team will verify this with your designated corporate enterprise administrator.
                    </p>
                    <div className="p-4 rounded-xl bg-gray-50 border border-gray-200 text-xs text-left space-y-1.5 text-gray-700">
                      <div><strong className="text-gray-900">Ticket ID:</strong> <span className="font-mono font-bold text-[#1C4D88]">{resultTicket || 'AXN-DEL-REGISTERED'}</span></div>
                      <div><strong className="text-gray-900">Registered Email:</strong> {formData.email}</div>
                      <div><strong className="text-gray-900">Current Status:</strong> <span className="inline-block px-2 py-0.5 rounded text-[10px] font-bold bg-amber-100 text-amber-800 border border-amber-300">Pending Review</span></div>
                      <div><strong className="text-gray-900">Resolution Window:</strong> Within 30 calendar days</div>
                    </div>
                    <button
                      onClick={() => {
                        setSubmitted(false);
                        setFormData({
                          fullName: '',
                          email: '',
                          companyName: '',
                          reason: 'Role change or departure',
                          additionalNotes: '',
                          confirmAcknowledged: false,
                        });
                      }}
                      className="text-xs text-[#1C4D88] font-bold hover:underline pt-2 block mx-auto"
                    >
                      Submit Another Request
                    </button>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                      <h3 className="text-lg font-bold text-gray-900">Web Deletion Form</h3>
                      <p className="text-xs text-gray-500">
                        Official self-service account deletion request form.
                      </p>
                    </div>

                    {submitError && (
                      <div className="p-3.5 rounded-xl bg-red-50 border border-red-200 text-red-700 text-xs flex items-start gap-2.5">
                        <AlertCircle className="w-4 h-4 shrink-0 mt-0.5 text-red-600" />
                        <div>
                          <p className="font-bold">Submission Notice</p>
                          <p className="text-[11px] mt-0.5">{submitError}</p>
                        </div>
                      </div>
                    )}


                    <div>
                      <label className="block text-xs font-bold text-gray-700 mb-1">
                        Full Name *
                      </label>
                      <input
                        type="text"
                        required
                        value={formData.fullName}
                        onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                        placeholder="John Doe"
                        className="w-full text-xs px-3.5 py-2.5 bg-gray-50 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-500 transition-colors"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-bold text-gray-700 mb-1">
                        Registered Corporate Email *
                      </label>
                      <input
                        type="email"
                        required
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        placeholder="engineer@company.com"
                        className="w-full text-xs px-3.5 py-2.5 bg-gray-50 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-500 transition-colors"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-bold text-gray-700 mb-1">
                        Organization / Energy Operator
                      </label>
                      <input
                        type="text"
                        value={formData.companyName}
                        onChange={(e) => setFormData({ ...formData, companyName: e.target.value })}
                        placeholder="e.g. Solar Energy Ltd."
                        className="w-full text-xs px-3.5 py-2.5 bg-gray-50 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-500 transition-colors"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-bold text-gray-700 mb-1">
                        Reason for Deletion
                      </label>
                      <select
                        value={formData.reason}
                        onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
                        className="w-full text-xs px-3.5 py-2.5 bg-gray-50 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-500 transition-colors"
                      >
                        <option value="Role change or departure">Role change or departure from plant</option>
                        <option value="No longer using the app">No longer using the mobile app</option>
                        <option value="Enterprise contract concluded">Enterprise contract concluded</option>
                        <option value="Privacy concerns">Privacy or data preferences</option>
                        <option value="Other">Other reasons</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-xs font-bold text-gray-700 mb-1">
                        Additional Notes (Optional)
                      </label>
                      <textarea
                        rows={3}
                        value={formData.additionalNotes}
                        onChange={(e) => setFormData({ ...formData, additionalNotes: e.target.value })}
                        placeholder="Mention any specific site codes or details..."
                        className="w-full text-xs px-3.5 py-2.5 bg-gray-50 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-500 transition-colors"
                      />
                    </div>

                    <div className="pt-2">
                      <label className="flex items-start gap-2 text-xs text-gray-600 cursor-pointer">
                        <input
                          type="checkbox"
                          required
                          checked={formData.confirmAcknowledged}
                          onChange={(e) => setFormData({ ...formData, confirmAcknowledged: e.target.checked })}
                          className="mt-0.5 rounded text-red-600 focus:ring-red-500"
                        />
                        <span>
                          I acknowledge that account deletion permanently deactivates my access, deletes credentials, and erases personal data within 30 days.
                        </span>
                      </label>
                    </div>

                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full mt-4 py-3 px-4 rounded-xl bg-red-600 hover:bg-red-700 text-white font-bold text-xs tracking-wide uppercase transition-all shadow-md hover:shadow-lg disabled:opacity-50 flex items-center justify-center gap-2"
                    >
                      {isSubmitting ? 'Processing Request...' : (
                        <>
                          <Trash2 className="w-4 h-4" /> Submit Deletion Request
                        </>
                      )}
                    </button>

                    <p className="text-[11px] text-gray-500 text-center mt-3">
                      Need personal assistance? Email our Grievance Officer at{' '}
                      <a href="mailto:privacy@axonmetric.com" className="text-[#1C4D88] font-bold underline">
                        privacy@axonmetric.com
                      </a>
                    </p>
                  </form>
                )}
              </div>
            </div>
          </div>
        </section>
      </div>
    </Layout>
  );
};

export default AccountDeletionPage;
